import React, { useState } from 'react';
import { X, Database } from 'lucide-react';
import { SchemaField, transactionSchema, invoicesSchema, subscriptionsSchema, subscriptionItemsSchema } from './utils/DataSchemas'

interface DatasetSchemaModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenSchemaDrawer?: () => void;
}

const DatasetSchemaModal: React.FC<DatasetSchemaModalProps> = ({ isOpen, onClose, onOpenSchemaDrawer }) => {

    const [activeTab, setActiveTab] = useState('transactions');

    const shouldShowComments = () => {
        const schema =
            activeTab === 'transactions'
                ? transactionSchema
                : activeTab === 'invoices'
                    ? invoicesSchema
                    : activeTab === 'subscriptions'
                        ? subscriptionsSchema
                        : subscriptionItemsSchema;

        return schema.some((f) => Boolean(f.comments && f.comments.trim().length > 0));
    };

    const renderSchemaTable = (schema: SchemaField[], title: string) => (
        <div className="mb-8 ms-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Database className="w-5 h-5 mr-2 text-blue-600" />
                {title}
            </h3>
            <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Field Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Data Type
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Field Type
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Value
                            </th>
                            {shouldShowComments() && (
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Comments
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {schema.map((field, index) => (
                            <tr key={field.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                    <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                        {field.name}
                                    </code>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                    <span className="px-2 py-1 rounded text-xs">
                                        {field.dataType}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                    <span className={field.fieldType.includes('Required') ? 'text-red-600 font-medium' : 'text-gray-600'}>
                                        {field.fieldType}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                    {field.examples}
                                </td>
                                {shouldShowComments() && (
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {field.comments ?? '-'}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 mt-4 bg-black bg-opacity-50 z-20 flex items-center justify-center p-4"
                onClick={onClose}
            />

            <div className={`fixed right-0 top-6 bottom-6 w-full h-full max-w-4xl bg-white rounded-xl shadow-xl z-20 transform transition-transform duration-300 flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="flex items-center p-6 border-b border-gray-200 bg-white sticky top-0">
                    <div className="flex items-center">
                        <Database className="w-6 h-6 text-blue-600 mr-3" />
                        <h2 className="text-xl font-semibold text-gray-900">DataModel Schema</h2>
                    </div>
                    <div className="flex ml-auto items-center">
                        {onOpenSchemaDrawer && (
                            <button
                                onClick={onOpenSchemaDrawer}
                                className="ml-auto text-blue-600 hover:text-blue-800 underline hover:underline-offset-2 bg-transparent px-0 py-0 text-sm font-medium transition-colors flex items-center gap-1"
                            >
                                <Database className="w-4 h-4" />
                                <span>Dev Guide: ER Diagram</span>
                            </button>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1 pb-20">
                    <div className="mb-6">
                        <p className="text-gray-600">
                            Below are the schema definitions for the dataset tables. These schemas define the structure and data types expected for each field.
                        </p>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            onClick={() => setActiveTab('transactions')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'transactions'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Transaction Schema
                        </button>
                        <button
                            onClick={() => setActiveTab('invoices')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'invoices'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Invoices Schema
                        </button>
                        <button
                            onClick={() => setActiveTab('subscriptions')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'subscriptions'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Subscriptions Schema
                        </button>
                        <button
                            onClick={() => setActiveTab('subscription-items')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'subscription-items'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Subscription Items Schema
                        </button>
                    </div>

                    {activeTab === 'transactions' && renderSchemaTable(transactionSchema, 'Transaction Schema')}
                    {activeTab === 'invoices' && renderSchemaTable(invoicesSchema, 'Invoices Schema')}
                    {activeTab === 'subscriptions' && renderSchemaTable(subscriptionsSchema, 'Subscriptions Schema')}
                    {activeTab === 'subscription-items' && renderSchemaTable(subscriptionItemsSchema, 'Subscription Items Schema')}
                </div>
            </div>
        </>
    );
};

export default DatasetSchemaModal;
